/**
 * Работа с деньгами.
 *
 * Единица хранения во всей системе — ЦЕЛАЯ КОПЕЙКА. Рубли с плавающей точкой
 * не используются нигде, кроме форматирования на экран и разбора пользовательского
 * ввода. Причина прозаична: 0.1 + 0.2 !== 0.3, а счета лаборатории уходят
 * в бухгалтерию клиник.
 */
/**
 * Разделитель разрядов — НЕРАЗРЫВНЫЙ пробел (U+00A0), как принято в русской
 * типографике: «12 500 ₽» не должно переноситься между 12 и 500.
 *
 * Задан явным escape, а не через `toLocaleString('ru-RU')`: результат последнего
 * зависит от сборки ICU в конкретной среде (обычный пробел, NBSP или узкий NBSP),
 * и серверный PDF расходился бы с браузерным превью.
 */
export const GROUP_SEPARATOR = ' ';
/** Пробельные символы, которые может содержать введённая пользователем сумма. */
const SPACE_CHARS = /[\s  ]/g;
/** Рубли (число или строка из поля ввода) → целые копейки. */
export function rublesToKop(rubles) {
    const n = typeof rubles === 'string' ? parseRublesInput(rubles) : rubles;
    if (!Number.isFinite(n))
        return 0;
    return Math.round(n * 100);
}
/** Копейки → рубли с дробной частью. Только для отображения и экспорта. */
export function kopToRubles(kop) {
    return kop / 100;
}
/**
 * Разбор пользовательского ввода суммы: «3 200,50», «3200.5», «12 500 ₽».
 * Пробелы (включая неразрывный), знак рубля и «руб.» отбрасываются,
 * запятая приводится к точке.
 */
export function parseRublesInput(input) {
    const cleaned = input
        .replace(SPACE_CHARS, '')
        .replace(/₽|руб\.?/gi, '')
        /*
         * Минус — ЛЮБОЙ из тех, что встречаются в полях.
         *
         * `formatKop` пишет отрицательные суммы типографским минусом «−», а
         * `Number` понимает только дефис. Поле с отрицательной корректировкой в
         * наряде показывало «−500», и стоило его тронуть, как сумма молча
         * превращалась в ноль: знак не читался, и весь ввод считался мусором.
         */
        .replace(/[−–]/g, '-')
        .replace(',', '.');
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
}
/** Группировка целой части по три разряда: `1234567` → `1 234 567`. */
function groupDigits(value) {
    return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEPARATOR);
}
/**
 * Формат суммы для интерфейса: `3 200` или `3 200,50`.
 * Копейки показываются только если они есть — как в документах лаборатории.
 */
export function formatKop(kop, opts = {}) {
    const negative = kop < 0;
    const abs = Math.abs(kop);
    const rub = Math.floor(abs / 100);
    const k = abs % 100;
    const rubStr = groupDigits(rub);
    const body = k > 0 || opts.alwaysKop ? `${rubStr},${String(k).padStart(2, '0')}` : rubStr;
    return negative ? `−${body}` : body;
}
/**
 * Формат суммы для ПЕЧАТНЫХ ДОКУМЕНТОВ: `2000.00`, `19500.00`, `259800.00`.
 *
 * Отличается от экранного намеренно, и это не небрежность:
 *   - точка, а не запятая;
 *   - копейки ВСЕГДА, даже нулевые;
 *   - без разделителей разрядов.
 *
 * Экранный формат с пробелами и запятой удобнее читать, но в документе он
 * выглядел бы как чужой бланк — бухгалтерия клиник сверяет присланное
 * с привычным. Неразрывный пробел вдобавок ломает поиск по сумме в Word.
 *
 * ОДИН на счёт и на акт. Некоторое время акт печатал «14 000,00», а счёт
 * «14000.00» — каждый повторял свой образец лаборатории. Образцы набирали
 * в разное время и в разных программах, но уходят счёт и акт вместе, одной
 * сделкой: две записи одной суммы в одном конверте — повод для вопроса
 * из бухгалтерии, а не признак аккуратности.
 */
export function formatKopDoc(kop) {
    const negative = kop < 0;
    const abs = Math.abs(kop);
    const body = `${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
    return negative ? `−${body}` : body;
}
/** Формат с знаком рубля для интерфейса. */
export function formatKopWithSign(kop) {
    return `${formatKop(kop)} ₽`;
}
/**
 * Сумма позиции счёта: цена × количество.
 * Количество может быть дробным (например, 1,5 часа работы), поэтому результат
 * округляется до копейки в момент вычисления, а не накапливает хвост.
 */
export function lineTotalKop(priceKop, qty) {
    return Math.round(priceKop * qty);
}
/**
 * Ставка на экране: «20», «22,5» — без хвостовых нулей и с запятой.
 *
 * Жила в разделе «Сотрудники», пока минимальная зарплата не заставила
 * показывать процент ещё и в «Финансах». Два экрана, показывающие одно и то
 * же число по-разному, — готовое расхождение, поэтому правило переехало сюда.
 */
export function formatPercent(value) {
    return String(Math.round(value * 100) / 100).replace('.', ',');
}
//# sourceMappingURL=money.js.map